/**
 * Heinrich 블로그 — 개별 글(본문) 페이지.
 * 다크 강제 + URL 기반 언어 + 좁은 리딩 컬럼(정갈한 에디토리얼) + 읽기 진행바.
 * SEO: Helmet + BlogPosting JSON-LD (AI 검색 인용 대상).
 */
import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link, useParams, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import ForceDarkTheme from '../../components/common/ForceDarkTheme'
import ForceLocale from '../../components/common/ForceLocale'
import { getPost, BLOG_UI, type Block } from './blogData'
import { fetchDbPost } from './blogApi'
import { trackView } from '../../lib/trackView'

function ReadingProgress() {
  const [w, setW] = useState(0)
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement
      setW((h.scrollTop / (h.scrollHeight - h.clientHeight)) * 100)
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])
  return <div className="fixed left-0 top-0 z-[60] h-[2px] bg-brand-gradient-r" style={{ width: `${w}%` }} />
}

function BlockView({ b }: { b: Block }) {
  switch (b.type) {
    case 'p':
      return <p className="text-[18px] leading-[1.78] text-ink-muted [&_em]:not-italic [&_em]:text-ink [&_em]:[box-shadow:inset_0_-0.5em_0_rgba(251,191,36,0.16)] [&_strong]:font-semibold [&_strong]:text-ink" dangerouslySetInnerHTML={{ __html: b.html }} />
    case 'h2': {
      // 게시글 하단 해시태그 푸터(네이버 이관분)는 h2 에 박혀 제목 크기로 나옴 →
      // 해시태그 3개 이상이면 본문보다 작고 연한 태그 행으로 렌더(선행 라벨어는 생략).
      const tags = b.text.match(/#[^\s#]+/g)
      if (tags && tags.length >= 3) {
        return (
          <div className="mt-12 flex flex-wrap gap-x-2.5 gap-y-1.5 border-t border-surface-border/40 pt-6">
            {tags.map((t, i) => (
              <span key={i} className="text-[13px] leading-relaxed text-ink-faint">{t}</span>
            ))}
          </div>
        )
      }
      return <h2 className="mt-14 text-[24px] font-bold tracking-tight text-ink">{b.text}</h2>
    }
    case 'img':
      return (
        <figure className="my-4">
          <div className="overflow-hidden rounded-xl border border-surface-border/40">
            <img src={b.src} alt={b.caption} className="w-full" loading="lazy" />
          </div>
          <figcaption className="mt-3 text-center text-[13px] leading-relaxed text-ink-faint">{b.caption}</figcaption>
        </figure>
      )
    case 'ul':
      return (
        <ul className="flex flex-col gap-2.5">
          {b.items.map((it, i) => (
            <li key={i} className="relative pl-6 text-[17.5px] leading-[1.7] text-ink-muted before:absolute before:left-0.5 before:top-[13px] before:h-[6px] before:w-[6px] before:rounded-full before:bg-brand-primary-light">
              {it}
            </li>
          ))}
        </ul>
      )
    case 'callout':
      return (
        <div className="rounded-2xl border border-surface-border/50 bg-surface-card/60 p-6 sm:p-7">
          <div className="mb-4 text-[11px] font-semibold uppercase tracking-[0.12em] text-brand-primary-light">{b.label}</div>
          <ul className="flex flex-col gap-2.5">
            {b.items.map((it, i) => (
              <li key={i} className="text-[15.5px] leading-relaxed text-ink-muted">{it}</li>
            ))}
          </ul>
        </div>
      )
    case 'pullquote':
      return <blockquote className="my-2 border-l-2 border-brand-primary-light py-1 pl-6 text-[22px] font-bold leading-snug tracking-tight text-ink">{b.text}</blockquote>
    case 'cta':
      // 단일 cta 는 그룹 렌더로 통일(아래 CtaGroupView). 여기 도달 시 폴백.
      return <CtaGroupView title={b.title} label={b.label} links={ctaLinks([b])} />
    default:
      return null
  }
}

// ── CTA 링크 그룹 ─────────────────────────────────────────────────────────────
// 원본 블로그의 "설명줄 + URL줄"이 각각 cta 블록으로 쪼개져 제목이 전부 동일하게 나오는 문제 →
// 연속 cta 를 하나의 '관련 링크' 섹션으로 묶고, 설명과 URL을 짝지어 링크마다 자기 설명을 표시.
type CtaBlock = { type: 'cta'; title: string; body: string; label: string; href: string }
type CtaLink = { text: string; href: string }

const _stripArrow = (s: string) => (s || '').replace(/^\s*👉\s*/, '').trim()
const _isUrl = (s: string) => /^https?:\/\//i.test(s)
const _cleanText = (s: string) => s.replace(/[:：]?\s*https?:\/\/\S+\s*$/i, '').trim() || s
const _prettyUrl = (s: string) => s.replace(/^https?:\/\//i, '').replace(/\/$/, '')

function ctaLinks(run: CtaBlock[]): CtaLink[] {
  const links: CtaLink[] = []
  let pending: CtaLink | null = null
  const flush = () => { if (pending) { links.push(pending); pending = null } }
  for (const c of run) {
    const body = _stripArrow(c.body)
    if (_isUrl(body)) {
      // URL 줄: 앞선 설명(일반 href)의 실제 링크로 붙인다.
      if (pending && !_isUrl(pending.href)) { pending.href = body; flush() }
      else { flush(); links.push({ text: _prettyUrl(body), href: body }) }
    } else {
      flush()
      pending = { text: _cleanText(body), href: c.href }
    }
  }
  flush()
  // /signup 단독 링크는 하단 버튼과 중복 → 제거. href 중복 제거.
  const seen = new Set<string>()
  return links.filter((l) => l.text && l.href !== '/signup' && !seen.has(l.href) && seen.add(l.href))
}

function CtaGroupView({ title, label, links }: { title: string; label: string; links: CtaLink[] }) {
  const linkCls = 'group inline-flex items-start gap-1.5 text-[16px] leading-relaxed text-brand-primary-light transition hover:text-ink'
  return (
    <div className="mt-6 rounded-2xl border border-brand-primary/30 bg-gradient-to-br from-brand-primary/10 to-transparent p-7 sm:p-8">
      <h3 className="text-[21px] font-bold text-ink">{title}</h3>
      {links.length > 0 && (
        <ul className="mt-4 flex flex-col gap-2">
          {links.map((l, i) => (
            <li key={i}>
              {_isUrl(l.href) ? (
                <a href={l.href} target="_blank" rel="noreferrer" className={linkCls}>
                  <span aria-hidden>→</span><span className="underline-offset-2 group-hover:underline">{l.text}</span>
                </a>
              ) : (
                <Link to={l.href} className={linkCls}>
                  <span aria-hidden>→</span><span className="underline-offset-2 group-hover:underline">{l.text}</span>
                </Link>
              )}
            </li>
          ))}
        </ul>
      )}
      <Link to="/signup" className="mt-6 inline-block rounded-[10px] bg-brand-gradient px-5 py-2.5 text-[14.5px] font-bold text-zinc-900">{label}</Link>
    </div>
  )
}

// 연속된 cta 블록을 하나의 CTA 그룹으로 묶어 렌더링 아이템 배열 생성.
type RenderItem = { kind: 'block'; block: Block } | { kind: 'cta'; title: string; label: string; links: CtaLink[] }
function groupBlocks(blocks: Block[]): RenderItem[] {
  const out: RenderItem[] = []
  let i = 0
  while (i < blocks.length) {
    if (blocks[i].type === 'cta') {
      const run: CtaBlock[] = []
      while (i < blocks.length && blocks[i].type === 'cta') { run.push(blocks[i] as CtaBlock); i++ }
      out.push({ kind: 'cta', title: run[0].title, label: run[0].label, links: ctaLinks(run) })
    } else {
      out.push({ kind: 'block', block: blocks[i] }); i++
    }
  }
  return out
}

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>()
  const { pathname } = useLocation()
  const lang: 'ko' | 'en' = pathname.startsWith('/en/') ? 'en' : 'ko'
  const base = lang === 'en' ? '/en/blog' : '/blog'
  const s = BLOG_UI[lang]
  // DB(API) 글 우선, 없으면 TS 번들 글 폴백
  const tsPost = slug ? getPost(slug, lang) : undefined
  const { data: dbPost, isLoading: dbLoading } = useQuery({
    queryKey: ['blog-post', slug, lang],
    queryFn: () => fetchDbPost(slug!, lang),
    enabled: !!slug,
    staleTime: 60_000,
  })
  const post = dbPost ?? tsPost

  // 조회수 비콘 — 게시글 진입 1회(일자별 누적)
  useEffect(() => { if (post?.slug) trackView('blog', post.slug) }, [post?.slug])

  if (!post) {
    // TS 폴백도 없고 DB 조회 중이면 로더, 끝났으면 not found
    if (dbLoading) {
      return (
        <>
          <ForceDarkTheme />
          <ForceLocale lang={lang} />
          <div className="mx-auto max-w-2xl px-6 py-28 text-center text-ink-faint">…</div>
        </>
      )
    }
    return (
      <>
        <ForceDarkTheme />
        <ForceLocale lang={lang} />
        <div className="mx-auto max-w-2xl px-6 py-28 text-center">
          <h1 className="text-2xl font-bold text-ink">{lang === 'en' ? 'Article not found' : '글을 찾을 수 없습니다'}</h1>
          <Link to={base} className="mt-6 inline-block text-sm text-brand-primary-light">← {s.backToList}</Link>
        </div>
      </>
    )
  }

  const canonical = `https://heinrich.dasancms.co.kr${base}/${post.slug}/`
  const altLang = lang === 'ko' ? 'en' : 'ko'
  const altBase = altLang === 'en' ? '/en/blog' : '/blog'
  const altCanonical = `https://heinrich.dasancms.co.kr${altBase}/${post.slug}`

  const jsonLd = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.standfirst,
    ...(post.hideDate ? {} : {
      datePublished: post.date.replace(/\./g, '-'),
      dateModified: post.date.replace(/\./g, '-'),
    }),
    image: `https://heinrich.dasancms.co.kr${post.cover}`,
    author: { '@type': 'Organization', name: 'Heinrich', url: 'https://heinrich.dasancms.co.kr/' },
    publisher: {
      '@type': 'Organization',
      name: 'Heinrich',
      url: 'https://heinrich.dasancms.co.kr/',
      logo: { '@type': 'ImageObject', url: 'https://heinrich.dasancms.co.kr/logo.png' },
    },
    inLanguage: post.lang,
    url: canonical,
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonical },
  })

  return (
    <>
      <ForceDarkTheme />
      <ForceLocale lang={lang} />
      <Helmet>
        <html lang={lang} />
        <title>{post.title} | Heinrich {lang === 'en' ? 'Blog' : '블로그'}</title>
        <meta name="description" content={post.standfirst} />
        <meta name="keywords" content={
          lang === 'ko'
            ? `${post.category},산업안전,위험성평가,KOSHA,안전관리,중대재해처벌법,Heinrich,하인리히`
            : `${post.category},industrial safety,risk assessment,KOSHA,EHS,safety management,Heinrich`
        } />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang={lang} href={canonical} />
        <link rel="alternate" hrefLang={altLang} href={altCanonical} />
        <link rel="alternate" hrefLang="x-default" href={`https://heinrich.dasancms.co.kr/blog/${post.slug}/`} />
        <meta property="og:site_name" content="Heinrich" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.standfirst} />
        <meta property="og:image" content={`https://heinrich.dasancms.co.kr${post.cover}`} />
        <meta property="og:image:alt" content={post.title} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        {!post.hideDate && <meta property="article:published_time" content={post.date.replace(/\./g, '-')} />}
        <meta property="article:author" content="Heinrich" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.standfirst} />
        <meta name="twitter:image" content={`https://heinrich.dasancms.co.kr${post.cover}`} />
        <script type="application/ld+json">{jsonLd}</script>
      </Helmet>

      <ReadingProgress />

      {/* ── 헤더 (중앙 정렬, 정갈) ── */}
      <header className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 bg-mesh" />
        <div className="relative mx-auto max-w-[46rem] px-6 pt-16 pb-10 text-center">
          <div className="text-[12.5px] text-ink-faint">
            <Link to={base} className="text-ink-muted transition hover:text-ink">{s.crumbBlog}</Link>
            <span className="mx-1.5">·</span>{post.category}
          </div>
          <h1 className="mx-auto mt-5 max-w-[24ch] text-[30px] font-extrabold leading-[1.14] tracking-tight text-ink sm:text-[40px]">
            {post.title}
          </h1>
          <p className="mx-auto mt-5 max-w-[56ch] text-[18.5px] leading-relaxed text-ink-muted">{post.standfirst}</p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3 text-[12.5px] text-ink-faint">
            <span className="flex items-center gap-2 text-ink-muted">
              <span className="h-[22px] w-[22px] rounded-full bg-brand-gradient" /> Heinrich Research
            </span>
            {!post.hideDate && (<><span aria-hidden>·</span><span>{post.date}</span></>)}
            <span aria-hidden>·</span><span>{s.read(post.readMin)}</span>
          </div>
        </div>
      </header>

      {/* ── 본문 ── */}
      <article className="mx-auto max-w-[42rem] px-6 pt-8">
        <div className="flex flex-col gap-6">
          {groupBlocks(post.blocks).map((it, i) =>
            it.kind === 'cta'
              ? <CtaGroupView key={i} title={it.title} label={it.label} links={it.links} />
              : <BlockView key={i} b={it.block} />,
          )}
        </div>
      </article>

      <div className="mx-auto max-w-[42rem] px-6 pb-24 pt-14">
        <div className="border-t border-surface-border/40 pt-8">
          <Link to={base} className="text-[14px] text-ink-muted transition hover:text-ink">← {s.backToList}</Link>
        </div>
      </div>
    </>
  )
}
